import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/common";
import AttachmentsPanel from "../components/AttachmentsPanel";
import AppointmentDialog from "../components/AppointmentDialog";
import { QUOTE_STATUSES, JOB_STATUSES, eur, itDate } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ArrowLeft, Phone, Mail, MapPin, MessageCircle, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function ClienteDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [c, setC] = useState(null);
  const [appts, setAppts] = useState([]);
  const [apptId, setApptId] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => { const { data } = await api.get(`/clients/${id}`); setC(data); };
  const loadAppts = async () => { try { const { data } = await api.get(`/appointments?client_id=${id}`); setAppts(data); } catch {} };
  useEffect(() => { load(); loadAppts(); /* eslint-disable-next-line */ }, [id]);
  if (!c) return <p className="text-muted-foreground animate-pulse">Caricamento…</p>;

  const toggleWa = async (v) => { await api.put(`/clients/${id}`, { whatsapp_opt_in: v }); setC({ ...c, whatsapp_opt_in: v }); toast.success("Preferenza WhatsApp aggiornata"); };
  const openEdit = () => { setForm({ name: c.name, phone: c.phone, email: c.email, address: c.address, locality: c.locality, notes: c.notes }); setEdit(true); };
  const saveEdit = async () => { if (!form.name) return toast.error("Nome obbligatorio"); const { data } = await api.put(`/clients/${id}`, form); setC({ ...c, ...data }); setEdit(false); toast.success("Cliente aggiornato"); };
  const removeClient = async () => { if (!window.confirm(`Eliminare il cliente ${c.name}? L'operazione è definitiva.`)) return; await api.delete(`/clients/${id}`); toast.success("Cliente eliminato"); nav("/clienti"); };

  return (
    <div>
      <button onClick={() => nav("/clienti")} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-brand"><ArrowLeft size={16} /> Clienti</button>

      <div className="card-soft p-5 mb-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="font-head text-2xl font-extrabold">{c.name}</h1>
            <div className="mt-2 space-y-1 text-sm text-muted-foreground">
              {c.phone && <p className="flex items-center gap-2"><Phone size={14} /> {c.phone}</p>}
              {c.email && <p className="flex items-center gap-2"><Mail size={14} /> {c.email}</p>}
              {(c.address || c.locality) && <p className="flex items-center gap-2"><MapPin size={14} /> {c.address} {c.locality}</p>}
            </div>
            {c.notes && <p className="mt-3 text-sm bg-accent rounded-lg p-3">{c.notes}</p>}
          </div>
          <div className="flex flex-col gap-2 items-end">
            {can("edit_clients") && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" data-testid="btn-edit-client" onClick={openEdit}><Pencil size={14} className="mr-1" /> Modifica</Button>
                <Button variant="outline" size="sm" data-testid="btn-delete-client" onClick={removeClient} className="text-destructive border-destructive/40 hover:bg-red-50"><Trash2 size={14} className="mr-1" /> Elimina</Button>
              </div>
            )}
            {c.phone && <a href={`https://wa.me/${c.phone.replace(/\D/g,"")}`} target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><MessageCircle size={14} className="mr-1" /> WhatsApp</Button></a>}
            <label className="flex items-center gap-2 text-xs text-muted-foreground"><Switch checked={c.whatsapp_opt_in} onCheckedChange={toggleWa} data-testid="wa-optin" /> Promemoria WhatsApp</label>
          </div>
        </div>
      </div>

      <Dialog open={edit} onOpenChange={setEdit}>
        <DialogContent data-testid="client-edit-dialog">
          <DialogHeader><DialogTitle className="font-head">Modifica cliente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[["name","Nome *"],["phone","Telefono"],["email","Email"],["address","Indirizzo"],["locality","Località"]].map(([k,l]) => (
              <div key={k}><Label>{l}</Label><Input data-testid={`edit-client-${k}`} value={form[k]||""} onChange={(e)=>setForm({...form,[k]:e.target.value})} /></div>
            ))}
            <div><Label>Note</Label><Textarea value={form.notes||""} onChange={(e)=>setForm({...form,notes:e.target.value})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setEdit(false)}>Annulla</Button><Button className="bg-brand text-white" data-testid="edit-client-save" onClick={saveEdit}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="preventivi">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="preventivi" data-testid="tab-preventivi">Preventivi ({c.quotes?.length||0})</TabsTrigger>
          <TabsTrigger value="lavori" data-testid="tab-lavori">Lavori ({c.jobs?.length||0})</TabsTrigger>
          <TabsTrigger value="appuntamenti" data-testid="tab-appuntamenti">Appuntamenti ({appts.length})</TabsTrigger>
          <TabsTrigger value="incassi" data-testid="tab-incassi">Incassi ({c.payments?.length||0})</TabsTrigger>
          <TabsTrigger value="allegati" data-testid="tab-allegati">Allegati ({c.attachments?.length||0})</TabsTrigger>
        </TabsList>

        <TabsContent value="preventivi" className="space-y-2">
          {c.quotes?.length ? c.quotes.map((q) => (
            <div key={q.id} className="card-soft p-3 flex items-center justify-between">
              <div><p className="font-medium text-sm">{q.code} · {q.product}</p><p className="text-xs text-muted-foreground">{eur(q.amount)} · creato {itDate(q.created_at)}</p></div>
              <StatusBadge list={QUOTE_STATUSES} value={q.status} />
            </div>
          )) : <p className="text-sm text-muted-foreground py-4">Nessun preventivo.</p>}
        </TabsContent>

        <TabsContent value="lavori" className="space-y-2">
          {c.jobs?.length ? c.jobs.map((j) => (
            <div key={j.id} className="card-soft p-3 flex items-center justify-between cursor-pointer lift" onClick={() => nav(`/lavori/${j.id}`)}>
              <div><p className="font-medium text-sm">{j.product}</p><p className="text-xs text-muted-foreground">{eur(j.agreed_amount)}</p></div>
              <StatusBadge list={JOB_STATUSES} value={j.status} />
            </div>
          )) : <p className="text-sm text-muted-foreground py-4">Nessun lavoro.</p>}
        </TabsContent>

        <TabsContent value="appuntamenti" className="space-y-2">
          {appts.length ? appts.map((a) => (
            <button key={a.id} data-testid={`client-appt-${a.id}`} onClick={() => setApptId(a.id)} className="w-full text-left card-soft p-3 flex items-center justify-between lift">
              <div>
                <p className="font-medium text-sm capitalize">{a.type} {a.status === "completato" ? "· svolto" : a.status === "annullato" ? "· annullato" : ""}</p>
                <p className="text-xs text-muted-foreground">{itDate(a.date)} · {a.start_time}{a.end_time ? `–${a.end_time}` : ""}</p>
              </div>
              {a.photo_count > 0 && <span className="text-xs text-brand">{a.photo_count} foto</span>}
            </button>
          )) : <p className="text-sm text-muted-foreground py-4">Nessun appuntamento.</p>}
        </TabsContent>

        <TabsContent value="incassi" className="space-y-2">
          {c.payments?.length ? c.payments.map((p) => (
            <div key={p.id} className="card-soft p-3 flex items-center justify-between">
              <div><p className="font-medium text-sm">{eur(p.amount)} · {p.kind}</p><p className="text-xs text-muted-foreground">{itDate(p.date)} · {p.method}</p></div>
            </div>
          )) : <p className="text-sm text-muted-foreground py-4">Nessun incasso.</p>}
        </TabsContent>

        <TabsContent value="allegati">
          <AttachmentsPanel clientId={c.id} canUpload={false} />
        </TabsContent>
      </Tabs>

      <AppointmentDialog appointmentId={apptId} open={!!apptId} onClose={() => setApptId(null)} onChanged={() => { loadAppts(); load(); }} />
    </div>
  );
}
